import React, { useEffect, useState, useContext } from 'react';

import { HandleAddActivity, HandleRemoveActivity } from '../components/crm/requests/ActivityRequests';
import { HandleAddNote, HandleRemoveNote, HandleUpdateNote } from '../components/crm/requests/NoteRequests';
import { HandleAddFile, HandleRemoveFile } from '../components/crm/requests/FileRequests';
import { updatePersonProps, updateOrganizationProps } from '../components/crm/shared/Functions';
import { GetPersonHistory, GetActivePerson } from '../components/crm/requests/PersonRequests';
import { GetDealsFromPerson } from '../components/crm/requests/DealRequests';
import { Loader } from '../components/crm/shared/CommonItems';
import { ActivityPanel } from '../components/crm/shared/DetailContent';
import { CompletedHistoryFetch } from '../components/crm/shared/Functions';
import { HistoryPanel } from '../components/crm/shared/HistoryItems';
import { ItemDetails, createPersonFieldComponents, createOrganizationFieldComponents } from '../components/crm/shared/ItemDetails';
import { DealsSummary } from '../components/crm/shared/DealsSummary';
import DetailHeader from '../components/crm/shared/DetailHeader';
import Toasts from '../components/crm/shared/Toasts';

import { DeepCopy } from '../components/tables/shared/TableEventHelpers';
import { scrollToHistoryAnchor } from '../components/crm/shared/field_inputs/shared/EventHelpers';

import { DataContext } from '../stores/crm-person-store';
import { DataContext as SharedDataContext } from '../stores/crm-store';
import { DataContext as OrgDataContext } from '../stores/crm-organization-store';
import { DataContext as DealDataContext } from '../stores/crm-deal-store';
import ErrorPage from './error';
import { useTranslation } from 'react-i18next';
import { ITEM_TITLE_DETAILS, ITEM_TITLE_ORGANIZATION, REMOVE_ACTIVITY_WARNING, REMOVE_FILE_WARNING, REMOVE_NOTE_WARNING } from '../i18n/keyConstants';

const CrmPersonDetails = ({ id }) => {
    const [error] = useState(null);
    const [, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isLoadingDeals, setIsLoadingDeals] = useState(false);
    const [activeContent, setActiveContent] = useState(0);
    const [deals, setDeals] = useState([]);
    const [activeHistoryContent, setActiveHistoryContent] = useState(0);
    const [historyCollection, setHistoryCollection] = useState({
        activities: [],
        notes: [],
        files: [],
        changelogs: [],
    });
    const {
        store: { activePerson, activityDealAlternatives, rows },
        dispatch,
    } = useContext(DataContext);
    const { store: sharedStore, dispatch: sharedDispatch } = useContext(SharedDataContext);
    const {
        store: { rows: orgRows },
        dispatch: orgDispatch,
    } = useContext(OrgDataContext);
    const {
        store: { pipelines, currentPipelineId },
        dispatch: dealDispatch,
    } = useContext(DealDataContext);
    const { t } = useTranslation(['itemDetails', 'removeContent']);
    const currentPath = window.location.pathname;

    /*
     * ===================================================================================================
     *                                       FUNCTIONS
     * ===================================================================================================
     */


    const finishedHistoryFetch = (res) => {
        if (res.success) {
            res = CompletedHistoryFetch(res, sharedStore);
            setHistoryCollection({ activities: res.data.activities, notes: res.data.notes, files: res.data.files, changelogs: res.data.changelogs });
        }

        setIsLoadingHistory(false);
    };

    const finishedPersonFetch = (res) => {
        if (res.success) {
            dispatch({
                type: 'UPDATE_ACTIVE_PERSON',
                payload: res.data,
            });
        }
        setIsLoading(false);
    };

    const finishedDealsFetch = (res) => {
        if (res.success) {
            setDeals(res.data);
            dispatch({
                type: 'UPDATE_ACTIVITY_DEAL_ALTERNATIVES',
                payload: res.data,
            });
        }

        setIsLoadingDeals(false);
    };

    async function PostActivity(activity) {
        let newPerson = DeepCopy(activePerson);

        activity.organizationId = activePerson.organizationId;
        activity.activityPersons = [
            {
                "type": 1,      // ActivityPersonType.Participant
                "personId": activePerson.id
            }
        ];

        setIsSaving(true);
        let response = await HandleAddActivity(activity, setIsSaving);

        if (response.success) {
            let activity = response.data;
            activity.organization = activePerson.organization;

            newPerson.activities.push(activity);

            dispatch({ type: 'UPDATE_ACTIVE_PERSON', payload: newPerson });
            dispatch({ type: 'SHOW_SAVED_TOAST' });
        } else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }
    }

    async function PostNote(note) {
        let newPerson = DeepCopy(activePerson);

        note.personId = activePerson.id;

        setIsSaving(true);
        let response = await HandleAddNote(note);
        setIsSaving(false);

        if (response.success) {
            let note = response.data;

            newPerson.notes.push(note);

            dispatch({ type: 'UPDATE_ACTIVE_PERSON', payload: newPerson });
            sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
        } else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }
    }

    async function RemoveNote(note) {
        var confirm = window.confirm(t(REMOVE_NOTE_WARNING));

        if (confirm) {
            let newPerson = DeepCopy(activePerson);

            setIsSaving(true);
            let response = await HandleRemoveNote(note);
            setIsSaving(false);

            if (response.success) {
                let note = response.data;

                newPerson.notes.splice(newPerson.notes.indexOf(note), 1);

                dispatch({ type: 'UPDATE_ACTIVE_PERSON', payload: newPerson });
                sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
            } else {
                sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
            }
        }
    }

    async function RemoveActivity(activity) {
        var confirm = window.confirm(t(REMOVE_ACTIVITY_WARNING));

        if (confirm) {
            let newPerson = DeepCopy(activePerson);

            setIsSaving(true);
            let response = await HandleRemoveActivity(activity, setIsSaving);

            if (response.success) {
                let activity = response.data;

                newPerson.activities.splice(newPerson.activities.indexOf(activity), 1);

                dispatch({ type: 'UPDATE_ACTIVE_PERSON', payload: newPerson });
                sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
            } else {
                sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
            }
        }
    }

    async function UpdateNote(newText, note) {
        let newNote = DeepCopy(note);
        newNote.text = newText;

        setIsSaving(true);
        let response = await HandleUpdateNote(newNote);
        setIsSaving(false);
        if (response.success) {
            sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
        } else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }

        return response;
    }

    async function PostFile(file) {
        let newPerson = DeepCopy(activePerson);

        file['personId'] = activePerson.id;
        file['ownerId'] = activePerson.ownerId;

        setIsSaving(true);
        let response = await HandleAddFile(file);
        setIsSaving(false);

        if (response.success) {
            let file = response.data;

            newPerson.files.push(file);

            dispatch({ type: 'UPDATE_ACTIVE_PERSON', payload: newPerson });
            sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
        } else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }
    }

    async function DownloadFile(file) {
        let url = `api/crmFileFront/${file.id}/download`;

        var link = document.createElement('a');
        link.setAttribute('target', '_blank');
        link.download = file.name;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    async function RemoveFile(file) {
        var confirm = window.confirm(t(REMOVE_FILE_WARNING));

        if (confirm) {
            let newPerson = DeepCopy(activePerson);

            setIsSaving(true);
            let response = await HandleRemoveFile(file);
            setIsSaving(false);

            if (response.success) {
                let file = response.data;

                newPerson.files.splice(newPerson.files.indexOf(file), 1);

                dispatch({ type: 'UPDATE_ACTIVE_PERSON', payload: newPerson });
                sharedDispatch({ type: 'SHOW_DELETED_TOAST' });
            } else {
                sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
            }
        }
    }

    const updatePerson = async (newProps) => {
        const response = await updatePersonProps(activePerson, newProps);
        if (response.success) {
            updateStore(response.person);
            dispatch({
                type: 'UPDATE_ACTIVE_PERSON',
                payload: response.person,
            });
        }
        return response;
    };

    const updateStore = (newPerson) => {
        const indexToUpdate = rows.findIndex((p) => p.id === newPerson.id);
        rows[indexToUpdate] = newPerson;

        if (indexToUpdate >= 0) {
            dispatch({ type: 'UPDATE_DATA', payload: rows });
        }
    };

    const updateOrganizationsInStore = (newPerson) => {
        if (typeof pipelines[currentPipelineId] !== undefined && pipelines[currentPipelineId]?.pipelineStages !== undefined) {
            const indexToUpdate = rows.findIndex((p) => p.id === newPerson.id);
            rows[indexToUpdate] = newPerson;

            if (indexToUpdate >= 0) {
                for (var i = 0; i < rows.length; i++) {
                    if (i !== indexToUpdate) {
                        if (rows[i].organization.id == newPerson.organization.id) {
                            rows[i].organization = newPerson.organization;
                        }
                    }
                }

                for (let j = 0; j < orgRows.length; j++) {
                    if (orgRows[j].id === newPerson.organization.id) {
                        orgRows[j] = newPerson.organization;
                    }
                }
                for (let d = 0; d < pipelines[currentPipelineId].pipelineStages.length; d++) {
                    const deal = pipelines[currentPipelineId].pipelineStages[d].deals;
                    for (let x = 0; x < deal.length; x++) {
                        if (deal[x].customer.id === newPerson.organization.id) {
                            deal[x].customer = newPerson.organization;
                        }
                    }
                }

                dispatch({ type: 'UPDATE_DATA', payload: rows });
                orgDispatch({ type: 'UPDATE_DATA', payload: orgRows });
                dealDispatch({
                    type: 'UPDATE_FETCH_PIPES',
                    pipelines: pipelines,
                });
            }
        }
    };

    const updateOrganization = async (newProps) => {
        const response = await updateOrganizationProps(activePerson.organization, newProps);
        if (response.success) {
            const newPerson = { ...activePerson };
            newPerson.organization = response.organization;
            updateOrganizationsInStore(newPerson);
            dispatch({
                type: 'UPDATE_ACTIVE_PERSON',
                payload: newPerson,
            });
        }
        return response;
    };

    useEffect(() => {
        if (deals !== activityDealAlternatives) {
            async function updateDealSummary() {
                if (activePerson !== null && activePerson.id === id) {
                    setIsLoadingDeals(true);
                    await GetDealsFromPerson(activePerson.id, finishedDealsFetch);
                }
            }
            updateDealSummary()
        }
    }, [activityDealAlternatives])

    const scrollToHistory = () => {
        const anchor = sharedStore.anchorTag;
        if (anchor) {
            const anchorEl = document.getElementById(anchor);
            if (anchorEl) {
                anchorEl.scrollIntoView();
                sharedDispatch({
                    type: 'SET_ANCHOR_TAG',
                    payload: null,
                });
            }
        }
    }

    /*
     * ===================================================================================================
     *                                       EFFECTS
     * ===================================================================================================
     */

    useEffect(() => {
        if (activePerson === null || activePerson.id !== id) {
            setIsLoading(true);
            GetActivePerson(id, finishedPersonFetch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        async function getPersonHistory() {
            if (activePerson !== null && activePerson.id === id) {
                setIsLoadingDeals(true);
                await GetDealsFromPerson(activePerson.id, finishedDealsFetch);

                setIsLoadingHistory(true);
                await GetPersonHistory(activePerson.id, finishedHistoryFetch);
            }
        }

        getPersonHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePerson]);

    useEffect(() => {
        if (activePerson === null || activePerson.id !== id) {
            setIsLoading(true);
            GetActivePerson(id, finishedPersonFetch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath]);

    useEffect(() => {
        const anchor = sharedStore.anchorTag;
        if (!isLoadingHistory && anchor) {
            scrollToHistoryAnchor(anchor, sharedDispatch);
        }
    }, [isLoadingHistory, sharedStore.anchorTag]);

    /*
     * ===================================================================================================
     *                                       COMPONENT
     * ===================================================================================================
     */

    if (error) {
        return <ErrorPage error={error} />;
    } else if (isLoading) {
        return (
            <section>
                <Loader />
            </section>
        );
    } else if (activePerson === null) {
        return null;
    }

    const content = (
        <>
            <section>
                <div className='d-flex flex-column h-100 crm-container-details'>
                    <DetailHeader item={activePerson} updateItemProps={updatePerson} itemType='person' />
                    <div className='d-flex flex-row'>
                        <div className='d-flex flex-column detail-card-info'>
                            <ItemDetails
                                title={t(ITEM_TITLE_DETAILS)}
                                components={createPersonFieldComponents(activePerson, sharedStore, updatePerson, t)}
                                type={t(ITEM_TITLE_DETAILS)}
                            />
                            {activePerson.organization && (
                                <ItemDetails
                                    title={t(ITEM_TITLE_ORGANIZATION)}
                                    components={createOrganizationFieldComponents(
                                        activePerson.organization,
                                        sharedStore,
                                        updateOrganization,
                                        t
                                    )}
                                    type={t(ITEM_TITLE_ORGANIZATION)}
                                />
                            )}
                            {isLoadingDeals ? (
                                <Loader />
                            ) : (
                                <DealsSummary item={activePerson} deals={deals} />
                            )}
                        </div>
                        <div className='d-flex flex-column detail-card-content'>
                            <ActivityPanel
                                activeContent={activeContent}
                                activeItem={activePerson}
                                setActiveContent={setActiveContent}
                                dealAlternatives={activityDealAlternatives}
                                PostActivity={PostActivity}
                                PostNote={PostNote}
                                PostFile={PostFile}
                                isPersonChoiceShowing={false}
                            />
                            {isLoadingHistory ? (
                                <Loader />
                            ) : (
                                <HistoryPanel
                                    historyCollection={historyCollection}
                                    activeItem={activePerson}
                                    activeHistoryContent={activeHistoryContent}
                                    setActiveHistoryContent={setActiveHistoryContent}
                                    removeNote={RemoveNote}
                                    updateNote={UpdateNote}
                                    removeActivity={RemoveActivity}
                                    removeFile={RemoveFile}
                                    downloadFile={DownloadFile}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </section>
            <Toasts />
        </>
    );
    return content;
};

export default CrmPersonDetails;
